import { useRetrieveListByGet } from 'src/compass/shared/hooks/reactQuery/useRetrieveListByGet';

const SITE_PROPERTIES_URL = 'Public/Properties/Site';

// Cached for the whole session (staleTime: Infinity). Feature flags are not here - they can be on
// for some users only, and this anonymous request has no user; see useFeatureFlags.
export default function useSiteProperties() {
  const query = useRetrieveListByGet({
    url: SITE_PROPERTIES_URL,
    staleTime: Infinity,
  });

  const databaseName = query.data?.databaseName?.toLowerCase();

  const isProductionDatabase = !databaseName || databaseName === 'production';

  // All empty unless the API is running against a provisioned environment's
  // database - the backend resolves that from the catalog it is connected to,
  // so the frontend does no environment lookup of its own.
  const documentation: string = query.data?.documentation ?? '';

  // A path relative to the API base (e.g. "Public/Properties/SiteAttachment"),
  // so it has to be resolved against REACT_APP_API_URL - which already ends in
  // "/api/" - to be usable as an href.
  const attachmentPath: string = query.data?.attachmentUrl ?? '';
  const attachmentUrl = attachmentPath
    ? `${process.env.REACT_APP_API_URL}${attachmentPath}`
    : '';

  // The name that download saves as, resolved server-side - so the dialog can
  // label the link with the filename the user will end up with.
  const attachmentFilename: string = query.data?.attachmentFilename ?? '';

  // The story the environment's branch is named for, parsed server-side from the
  // branch - opened in Azure DevOps by the dialog's Story button.
  const storyNumber: number | null = query.data?.storyNumber ?? null;
  const storyUrl = storyNumber
    ? `https://dev.azure.com/cas-organization/Web%20App/_workitems/edit/${storyNumber}`
    : '';

  // Whether there is anything for the header's environment dialog to show. When
  // false the header tag stays a plain label, which is the case on dev2,
  // production and ordinary local dev.
  const hasEnvironmentInfo = documentation.trim() !== '' || attachmentUrl !== '' || storyUrl !== '';

  // Every email and text this site sends goes to the logged-in user rather than its recipients -
  // true everywhere but Prod. The backend setting itself, so it can disagree with
  // isProductionDatabase (a local backend on Prod data still redirects). False until loaded.
  const sendAllEmailsToLoggedIn: boolean = query.data?.sendAllEmailsToLoggedIn ?? false;

  return {
    ...query,
    isProductionDatabase,
    documentation,
    attachmentUrl,
    attachmentFilename,
    storyUrl,
    hasEnvironmentInfo,
    sendAllEmailsToLoggedIn,
  };
}
